import React, { useState } from 'react';
import { makeStyles, Theme } from '@material-ui/core';
import { colors } from '@lifesize/ux-assets';
import { fade } from '@material-ui/core/styles';
import clsx from 'clsx';
import { ReactComponent as ChevronDownIcon } from '@lifesize/ux-assets/vectors/svgIcons/chevron-down.svg';
import { ReactComponent as ChevronUpIcon } from '@lifesize/ux-assets/vectors/svgIcons/chevron-up.svg';

interface Props {
  dataTestId: string;
  showExpandIcon: boolean;
  header: string;
  children: React.ReactNode;
  fillClass?: 'fill50' | 'fillRemaining';
  collapseList?: boolean;
  noScroll: boolean;
}

const PanelList = ({
  dataTestId,
  showExpandIcon,
  header,
  children,
  fillClass,
  collapseList = false,
  noScroll = false
}: Props) => {
  const classes = useStyles({});
  const [isListHidden, setIsListHidden] = useState(collapseList);
  const ChevronIcon = isListHidden ? ChevronUpIcon : ChevronDownIcon;

  return (
    <div className={clsx(classes.listContainer, fillClass || 'fill50')}>
      <header className={clsx(classes.listTitle, classes.flexHeader)}>
        <h6 data-testid={dataTestId}>{header}</h6>
        {showExpandIcon && <ChevronIcon onClick={() => setIsListHidden(!isListHidden)} />}
      </header>
      <div className={clsx(classes.list, { [classes.isHidden]: isListHidden, [classes.noScroll]: noScroll })}>
        {children}
      </div>
    </div>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  listContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: 'auto',
    position: 'relative',
    marginTop: theme.spacing(2),
    paddingBottom: theme.spacing(1),
    '&.fill50': {
      flex: '0 1 auto',
      maxHeight: '50%'
    },
    '&.fillRemaining': {
      flex: '1 1 auto'
    },
    '&:after': {
      backgroundImage: `linear-gradient(${fade(colors.white, 0)}, ${fade(colors.white, 1)})`,
      bottom: 0,
      content: '',
      height: theme.spacing(2.5),
      left: 0,
      position: 'absolute',
      right: 0
    }
  },
  listTitle: {
    color: colors.squadronBlue,
    flex: '0 0 auto',
    padding: `${theme.spacing(0)}px ${theme.spacing(3)}px 0`,
    textAlign: 'left',
    textTransform: 'uppercase',
    '& h6': {
      fontSize: '1em',
      fontWeight: theme.typography.fontWeightBold,
      flex: '0 1 auto',
      textAlign: 'left'
    },
    '&.isLoading': {
      textAlign: 'center'
    },
    '& > svg': {
      color: colors.squadronBlue,
      cursor: 'pointer',
      height: '1.2rem',
      flex: '0 0 auto',
      marginLeft: theme.spacing(1),
      width: 'auto'
    }
  },
  flexHeader: {
    display: 'flex'
  },
  /*  emptyListMessage: {
    fontSize: '1.2em',
    padding: `0 ${theme.spacing(3)}px`
  },*/
  list: {
    flex: '1 1 auto',
    marginTop: theme.spacing(1),
    overflowX: 'hidden',
    overflowY: 'auto',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    textAlign: 'left'
  },
  isHidden: {
    display: 'none',
    margin: theme.spacing(1, 0, 1, 0)
  },
  noScroll: {
    overflow: 'unset'
  }
}));

export default PanelList;
